import { useCallback, useEffect, useId, useState } from 'react'
import type { JSX, ReactNode } from 'react'

import { RESOURCE_KINDS } from '@lensmira/domain'
import type { MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import type { Translation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { DataSurface } from './DataSurface.js'

/**
 * What the shop offers for appointments, and who gives it. §6.14 `APT-001`, `APT-002`.
 *
 * Appointment types, the rooms and people that appointments book besides a time, and a visiting
 * optometrist's visit dates. The routes were all on `appointment-config.controller.ts` and no
 * screen called one, so `appointment_type`, `appointment_resource` and `appointment_resource_visit`
 * could be written by an API and never by a shop.
 *
 * ---
 *
 * **Owner alone, reads included.** Every route is `tenant_settings_policies` (`decisions/0034`), so
 * a manager who opens this gets the sentence saying to ask the owner and the loaders are never
 * called: a read that answers 403 would otherwise read as "could not read your types".
 *
 * **A visiting optometrist with no visit dates is a trap, and the row says so.** Booking reads the
 * dates for that kind alone and zero rows means no day at all. The list loads each active one's
 * dates so the warning is on the resource row, where somebody setting up the shop will see it,
 * and not only inside the dates editor they have no reason to open.
 *
 * **Three things the server accepts and the screen must not send.** A staff calendar with no staff
 * member (a calendar tied to nobody), a second type or resource with a name already in use (neither
 * table has a unique constraint, so two with one name cannot be told apart at the counter), and a
 * staff member given a second calendar in use at the branch (a 409 that carries the existing row,
 * which halts the write queue). Each is refused here before anything is queued.
 *
 * **Removing a visit date that still has a booked appointment is refused 409**, and no route says
 * which dates are booked. Until a read exists the copy warns before the save.
 *
 * **`visit-dates` is versioned by the resource's version**, which rename, stop and use-again also
 * bump. The editor reads the dates afresh when it opens and the list reads again once any write
 * settles, so a form never carries a version another write has already spent.
 */

export interface AppointmentTypeRow {
  readonly id: string
  readonly name: string
  readonly durationMinutes: number
  readonly requiredResourceKinds: readonly string[]
  readonly archivedAt: string | null
  readonly version: number
}

export interface AppointmentResourceRow {
  readonly id: string
  readonly branchId: string
  readonly kind: string
  readonly name: string
  readonly staffId: string | null
  readonly archivedAt: string | null
  readonly version: number
}

export interface VisitDatesRow {
  readonly resourceId: string
  /** `YYYY-MM-DD`, sorted. */
  readonly dates: readonly string[]
  /** The resource's own version. */
  readonly version: number
}

/** A person a staff calendar can be tied to. `StaffRow` from `readStaff` satisfies it. */
export interface StaffOption {
  readonly id: string
  readonly displayName: string
  readonly offboardedAt: string | null
}

export interface AppointmentOffersProps {
  /** `tenant_settings_policies`: the owner alone, reads included. */
  readonly canManage: boolean
  readonly branchId: string
  readonly branchName: string
  readonly loadTypes: () => Promise<readonly AppointmentTypeRow[]>
  readonly loadResources: (branchId: string) => Promise<readonly AppointmentResourceRow[]>
  readonly loadVisitDates: (resourceId: string) => Promise<VisitDatesRow>
  readonly loadStaff: () => Promise<readonly StaffOption[]>
}

/** The server's cap: 731 days is two years with a day to spare. */
const MAX_VISIT_DATES = 731
const MAX_NAME = 120
const DAY_MINUTES = 1440

type Scope = 'typeAdd' | 'typeEdit' | 'resourceAdd' | 'resourceEdit' | 'toggle' | 'dates'

const kindWord = (t: Translation['t'], kind: string): string => {
  switch (kind) {
    case 'staff':
      return t('resourceKind.staff')
    case 'testing_room':
      return t('resourceKind.testingRoom')
    case 'visiting_optometrist':
      return t('resourceKind.visitingOptometrist')
    default:
      return kind
  }
}

const kindMeans = (t: Translation['t'], kind: string): string | null => {
  switch (kind) {
    case 'staff':
      return t('apptOffers.kindMeans.staff')
    case 'testing_room':
      return t('apptOffers.kindMeans.testingRoom')
    case 'visiting_optometrist':
      return t('apptOffers.kindMeans.visitingOptometrist')
    default:
      return null
  }
}

const sameName = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase()

const nameProblem = (name: string): boolean => name.trim() === '' || name.trim().length > MAX_NAME

function Field({
  id,
  label,
  children,
}: {
  readonly id: string
  readonly label: string
  readonly children: ReactNode
}): JSX.Element {
  return (
    <div className="lm-field">
      <label htmlFor={id} className="lm-field__label">
        {label}
      </label>
      {children}
    </div>
  )
}

const Problem = ({ text }: { readonly text: string | null }): JSX.Element | null =>
  text === null ? null : (
    <span className="lm-field__error" role="alert">
      {text}
    </span>
  )

interface TypeDraft {
  readonly name: string
  readonly minutes: string
  readonly kinds: readonly string[]
}
const EMPTY_TYPE: TypeDraft = { name: '', minutes: '', kinds: [] }

/**
 * Which thing the queue line is about.
 *
 * This screen writes types, resources and visit dates, so one fixed subject would say "The change"
 * for all three. `pending.scope` already distinguishes them, so the line reads "The visit dates are
 * saved" rather than something a shopkeeper has to guess at.
 */
const SUBJECT: Readonly<Record<Scope, MessageKey>> = {
  typeAdd: 'queue.subject.appointmentType',
  typeEdit: 'queue.subject.appointmentType',
  resourceAdd: 'queue.subject.appointmentResource',
  resourceEdit: 'queue.subject.appointmentResource',
  toggle: 'queue.subject.appointmentResource',
  dates: 'queue.subject.visitDates',
}

export function AppointmentOffers({
  canManage,
  branchId,
  branchName,
  loadTypes,
  loadResources,
  loadVisitDates,
  loadStaff,
}: AppointmentOffersProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const ids = useId()

  const [types, setTypes] = useState<readonly AppointmentTypeRow[] | null>(null)
  const [typesFailed, setTypesFailed] = useState(false)
  const [resources, setResources] = useState<readonly AppointmentResourceRow[] | null>(null)
  const [resourcesFailed, setResourcesFailed] = useState(false)
  const [staff, setStaff] = useState<readonly StaffOption[] | null>(null)
  const [staffFailed, setStaffFailed] = useState(false)
  const [visits, setVisits] = useState<Readonly<Record<string, VisitDatesRow>>>({})
  const [visitsFailed, setVisitsFailed] = useState<readonly string[]>([])
  const [showStopped, setShowStopped] = useState(false)

  const [typeAdd, setTypeAdd] = useState<TypeDraft>(EMPTY_TYPE)
  const [typeAddError, setTypeAddError] = useState<string | null>(null)
  const [typeEditing, setTypeEditing] = useState<string | null>(null)
  const [typeEdit, setTypeEdit] = useState<TypeDraft>(EMPTY_TYPE)
  const [typeEditError, setTypeEditError] = useState<string | null>(null)

  const [resKind, setResKind] = useState('')
  const [resName, setResName] = useState('')
  const [resStaff, setResStaff] = useState('')
  const [resAddError, setResAddError] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [renameTo, setRenameTo] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)

  const [datesFor, setDatesFor] = useState<AppointmentResourceRow | null>(null)
  const [datesRow, setDatesRow] = useState<VisitDatesRow | null>(null)
  const [datesFailed, setDatesFailed] = useState(false)
  const [datesDraft, setDatesDraft] = useState<readonly string[]>([])
  const [pick, setPick] = useState('')
  const [datesError, setDatesError] = useState<string | null>(null)

  const [pending, setPending] = useState<{ readonly id: string; readonly scope: Scope } | null>(
    null,
  )

  const refreshTypes = useCallback(() => {
    loadTypes().then(
      (rows) => {
        setTypes(rows)
        setTypesFailed(false)
      },
      () => {
        // Its own state, not an empty list: "no types yet" and "could not be read" are different
        // sentences, and a form error must never turn this into a failed read.
        setTypes(null)
        setTypesFailed(true)
      },
    )
  }, [loadTypes])

  const refreshResources = useCallback(() => {
    loadResources(branchId).then(
      (rows) => {
        setResources(rows)
        setResourcesFailed(false)
        const visiting = rows.filter(
          (row) => row.kind === 'visiting_optometrist' && row.archivedAt === null,
        )
        void Promise.allSettled(visiting.map((row) => loadVisitDates(row.id))).then((results) => {
          const found: Record<string, VisitDatesRow> = {}
          const failed: string[] = []
          results.forEach((result, position) => {
            const row = visiting[position]
            if (row === undefined) return
            if (result.status === 'fulfilled') found[row.id] = result.value
            else failed.push(row.id)
          })
          setVisits(found)
          setVisitsFailed(failed)
        })
      },
      () => {
        setResources(null)
        setResourcesFailed(true)
      },
    )
  }, [loadResources, loadVisitDates, branchId])

  const refreshStaff = useCallback(() => {
    loadStaff().then(
      (rows) => {
        setStaff(rows)
        setStaffFailed(false)
      },
      () => {
        setStaff(null)
        setStaffFailed(true)
      },
    )
  }, [loadStaff])

  useEffect(() => {
    if (!canManage) return
    refreshTypes()
  }, [canManage, refreshTypes])

  useEffect(() => {
    if (!canManage) return
    refreshResources()
  }, [canManage, refreshResources])

  useEffect(() => {
    if (!canManage) return
    refreshStaff()
  }, [canManage, refreshStaff])

  const openDates = useCallback(
    (resource: AppointmentResourceRow) => {
      setDatesFor(resource)
      setDatesRow(null)
      setDatesFailed(false)
      setDatesDraft([])
      setPick('')
      setDatesError(null)
      loadVisitDates(resource.id).then(
        (row) => {
          setDatesRow(row)
          setDatesDraft([...row.dates].sort())
        },
        () => {
          setDatesFailed(true)
        },
      )
    },
    [loadVisitDates],
  )

  useWriteSettled(snapshot, pending?.id ?? null, () => {
    const scope = pending?.scope
    setPending(null)
    if (scope === 'typeAdd') {
      setTypeAdd(EMPTY_TYPE)
      setTypeAddError(null)
    }
    if (scope === 'typeEdit') {
      setTypeEditing(null)
      setTypeEditError(null)
    }
    if (scope === 'resourceAdd') {
      setResKind('')
      setResName('')
      setResStaff('')
      setResAddError(null)
    }
    if (scope === 'resourceEdit') {
      setRenaming(null)
      setRenameError(null)
    }
    if (scope === 'dates') setDatesFor(null)
    // Every version on screen is now stale, and the next change would be a 409.
    refreshTypes()
    refreshResources()
  })

  /**
   * A write somebody discarded has to give the forms back. `useWriteSettled` fires only for a
   * write the server took, so one thrown away in `BlockedWrites` would leave `pending` set and
   * every button disabled for good.
   */
  useEffect(() => {
    if (pending === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === pending.id)) setPending(null)
  }, [snapshot, pending])

  const send = useCallback(
    async (
      scope: Scope,
      request: {
        readonly method: 'POST' | 'PATCH' | 'PUT'
        readonly path: string
        readonly label: string
        readonly body: Record<string, unknown>
      },
    ) => {
      const write = (await enqueue(request)) as { id: string }
      setPending({ id: write.id, scope })
    },
    [enqueue],
  )

  /* ------------------------------ types ------------------------------ */

  /** The body of a type, or null after saying why not. `except` is the type being changed. */
  const shapeType = (
    draft: TypeDraft,
    except: string | null,
    fail: (message: string) => void,
  ): { name: string; durationMinutes: number; requiredResourceKinds: string[] } | null => {
    const name = draft.name.trim()
    if (nameProblem(name)) {
      fail(t('apptOffers.typeNameRequired'))
      return null
    }
    const minutes = draft.minutes.trim()
    const count = /^\d+$/.test(minutes) ? Number(minutes) : Number.NaN
    if (!Number.isInteger(count) || count < 1 || count > DAY_MINUTES) {
      fail(t('apptOffers.typeMinutesShape'))
      return null
    }
    if ((types ?? []).some((other) => other.id !== except && sameName(other.name, name))) {
      fail(t('apptOffers.typeNameTaken', { name }))
      return null
    }
    return {
      name,
      durationMinutes: count,
      requiredResourceKinds: [...new Set(draft.kinds)].sort(),
    }
  }

  const addType = async (): Promise<void> => {
    const body = shapeType(typeAdd, null, setTypeAddError)
    if (body === null) return
    setTypeAddError(null)
    await send('typeAdd', {
      method: 'POST',
      path: '/appointment-types',
      label: t('apptOffers.queueAddType', { name: body.name }),
      body,
    })
  }

  const changeType = async (row: AppointmentTypeRow): Promise<void> => {
    const body = shapeType(typeEdit, row.id, setTypeEditError)
    if (body === null) return
    const unchanged =
      body.name === row.name &&
      body.durationMinutes === row.durationMinutes &&
      body.requiredResourceKinds.join(',') === [...row.requiredResourceKinds].sort().join(',')
    if (unchanged) {
      setTypeEditError(t('apptSetup.nothingChanged'))
      return
    }
    setTypeEditError(null)
    await send('typeEdit', {
      method: 'PATCH',
      path: `/appointment-types/${encodeURIComponent(row.id)}`,
      label: t('apptOffers.queueChangeType', { name: row.name }),
      body: { ...body, expectedVersion: row.version },
    })
  }

  const toggleType = async (row: AppointmentTypeRow, stop: boolean): Promise<void> => {
    await send('toggle', {
      method: 'POST',
      path: `/appointment-types/${encodeURIComponent(row.id)}/${stop ? 'archive' : 'restore'}`,
      label: t(stop ? 'apptOffers.queueStopType' : 'apptOffers.queueResumeType', {
        name: row.name,
      }),
      body: { expectedVersion: row.version },
    })
  }

  const kindBoxes = (
    draft: TypeDraft,
    set: (next: TypeDraft) => void,
    prefix: string,
  ): JSX.Element => (
    <fieldset className="lm-fieldset">
      <legend className="lm-field__label">{t('apptOffers.typeNeeds')}</legend>
      <p className="lm-hint">{t('apptOffers.typeNeedsMeans')}</p>
      {RESOURCE_KINDS.map((kind) => (
        <label key={kind} className="lm-field" htmlFor={`${prefix}-${kind}`}>
          <input
            id={`${prefix}-${kind}`}
            type="checkbox"
            checked={draft.kinds.includes(kind)}
            onChange={(e) => {
              const on = e.currentTarget.checked
              set({
                ...draft,
                kinds: on ? [...draft.kinds, kind] : draft.kinds.filter((k) => k !== kind),
              })
            }}
          />
          {kindWord(t, kind)}
        </label>
      ))}
    </fieldset>
  )

  const typeFields = (
    draft: TypeDraft,
    set: (next: TypeDraft) => void,
    prefix: string,
  ): JSX.Element => (
    <>
      <Field id={`${prefix}-name`} label={t('apptOffers.typeName')}>
        <input
          id={`${prefix}-name`}
          className="lm-input"
          dir="auto"
          autoComplete="off"
          value={draft.name}
          onChange={(e) => {
            set({ ...draft, name: e.currentTarget.value })
          }}
        />
      </Field>
      <Field id={`${prefix}-minutes`} label={t('apptOffers.typeMinutes')}>
        <input
          id={`${prefix}-minutes`}
          className="lm-input"
          dir="ltr"
          inputMode="numeric"
          autoComplete="off"
          value={draft.minutes}
          onChange={(e) => {
            set({ ...draft, minutes: e.currentTarget.value })
          }}
        />
      </Field>
      {kindBoxes(draft, set, prefix)}
    </>
  )

  /* ---------------------------- resources ---------------------------- */

  const staffName = (id: string | null): string => {
    if (id === null) return t('apptOffers.staffUnnamed')
    return staff?.find((person) => person.id === id)?.displayName ?? t('apptOffers.staffUnnamed')
  }

  /** The other calendar in use for this person at this branch, if there is one. */
  const staffTwin = (
    staffId: string | null,
    except: string | null,
  ): AppointmentResourceRow | undefined =>
    staffId === null
      ? undefined
      : (resources ?? []).find(
          (row) =>
            row.id !== except &&
            row.kind === 'staff' &&
            row.staffId === staffId &&
            row.archivedAt === null,
        )

  const addResource = async (): Promise<void> => {
    if (resKind === '') {
      setResAddError(t('apptOffers.resourceKindRequired'))
      return
    }
    const name = resName.trim()
    if (nameProblem(name)) {
      setResAddError(t('apptOffers.resourceNameRequired'))
      return
    }
    if (resKind === 'staff' && resStaff === '') {
      // A staff calendar tied to nobody is accepted by the server and is a calendar nobody works.
      // The catalogue has no sentence for it yet (`keysMissing`), so the label asks the question.
      setResAddError(t('apptOffers.resourceStaff'))
      return
    }
    if ((resources ?? []).some((row) => sameName(row.name, name))) {
      setResAddError(t('apptOffers.resourceNameTaken', { name, branch: branchName }))
      return
    }
    if (resKind === 'staff' && staffTwin(resStaff, null) !== undefined) {
      // The server answers a second calendar for one person with a 409, which halts the queue.
      setResAddError(
        t('apptOffers.resourceStaffTaken', { person: staffName(resStaff), branch: branchName }),
      )
      return
    }
    setResAddError(null)
    await send('resourceAdd', {
      method: 'POST',
      path: `/branches/${encodeURIComponent(branchId)}/appointment-resources`,
      label: t('apptOffers.queueAddResource', { name }),
      // `staffId` only for a staff calendar: the server refuses it on any other kind.
      body: { kind: resKind, name, ...(resKind === 'staff' ? { staffId: resStaff } : {}) },
    })
  }

  const renameResource = async (row: AppointmentResourceRow): Promise<void> => {
    const name = renameTo.trim()
    if (nameProblem(name)) {
      setRenameError(t('apptOffers.resourceNameRequired'))
      return
    }
    if (name === row.name) {
      setRenameError(t('apptSetup.nothingChanged'))
      return
    }
    if ((resources ?? []).some((other) => other.id !== row.id && sameName(other.name, name))) {
      setRenameError(t('apptOffers.resourceNameTaken', { name, branch: branchName }))
      return
    }
    setRenameError(null)
    await send('resourceEdit', {
      method: 'PATCH',
      path: `/appointment-resources/${encodeURIComponent(row.id)}`,
      label: t('apptOffers.queueRenameResource', { name: row.name }),
      body: { name, expectedVersion: row.version },
    })
  }

  const toggleResource = async (row: AppointmentResourceRow, stop: boolean): Promise<void> => {
    await send('toggle', {
      method: 'POST',
      path: `/appointment-resources/${encodeURIComponent(row.id)}/${stop ? 'archive' : 'restore'}`,
      label: t(stop ? 'apptOffers.queueStopResource' : 'apptOffers.queueResumeResource', {
        name: row.name,
      }),
      body: { expectedVersion: row.version },
    })
  }

  /* --------------------------- visit dates --------------------------- */

  const addDate = (): void => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(pick) || pick < '0001-01-01') {
      setDatesError(t('visitDates.pickFirst'))
      return
    }
    if (datesDraft.includes(pick)) {
      setDatesError(t('visitDates.already', { date: pick }))
      return
    }
    if (datesDraft.length >= MAX_VISIT_DATES) {
      setDatesError(t('visitDates.tooMany'))
      return
    }
    setDatesError(null)
    setDatesDraft([...datesDraft, pick].sort())
    setPick('')
  }

  const saveDates = async (): Promise<void> => {
    if (datesFor === null || datesRow === null) return
    if (datesDraft.length > MAX_VISIT_DATES) {
      setDatesError(t('visitDates.tooMany'))
      return
    }
    const saved = [...datesRow.dates].sort().join(',')
    if (saved === [...datesDraft].sort().join(',')) {
      setDatesError(t('apptSetup.nothingChanged'))
      return
    }
    setDatesError(null)
    await send('dates', {
      method: 'PUT',
      path: `/appointment-resources/${encodeURIComponent(datesFor.id)}/visit-dates`,
      label: t('visitDates.queueSave', { name: datesFor.name }),
      body: { dates: [...datesDraft].sort(), expectedVersion: datesRow.version },
    })
  }

  const busy = pending !== null
  const line = statusLine(t, snapshot, pending?.id ?? null, t(pending === null ? 'queue.subject.setup' : SUBJECT[pending.scope]))

  /* ------------------------------ screen ------------------------------ */

  if (!canManage) {
    return (
      <section className="lm-screen" aria-labelledby={`${ids}-heading`}>
        <h1 id={`${ids}-heading`} className="lm-screen__heading">
          {t('apptOffers.heading')}
        </h1>
        <DataSurface
          state={{ kind: 'denied' }}
          shape="rows"
          what={t('apptOffers.typesHeading')}
          deniedMessage={t('apptSetup.ownerOnly')}
        >
          {null}
        </DataSurface>
      </section>
    )
  }

  if (datesFor !== null) {
    const removed = datesRow === null ? [] : datesRow.dates.filter((d) => !datesDraft.includes(d))
    const changed =
      datesRow !== null && [...datesRow.dates].sort().join(',') !== [...datesDraft].sort().join(',')
    return (
      <section className="lm-screen" aria-labelledby={`${ids}-dates-heading`}>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setDatesFor(null)
          }}
        >
          {t('visitDates.back')}
        </button>
        <h1 id={`${ids}-dates-heading`} className="lm-screen__heading" dir="auto">
          {t('visitDates.heading', { name: datesFor.name })}
        </h1>
        <p className="lm-hint">{t('visitDates.means')}</p>

        <DataSurface
          state={
            datesFailed
              ? {
                  kind: 'error',
                  message: t('visitDates.loadFailed', { name: datesFor.name }),
                  onRetry: () => {
                    openDates(datesFor)
                  },
                }
              : datesRow === null
                ? { kind: 'loading' }
                : datesDraft.length === 0
                  ? { kind: 'empty' }
                  : { kind: 'ready' }
          }
          shape="rows"
          rows={3}
          what={t('visitDates.heading', { name: datesFor.name })}
          emptyMessage={t('visitDates.empty', { name: datesFor.name })}
          filteredMessage={t('visitDates.empty', { name: datesFor.name })}
          deniedMessage={t('apptSetup.ownerOnly')}
        >
          <ul className="lm-supplier-list">
            {datesDraft.map((date) => (
              <li key={date} className="lm-supplier">
                <span className="lm-supplier__name">
                  <Ltr>{date}</Ltr>
                </span>
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    setDatesDraft(datesDraft.filter((d) => d !== date))
                  }}
                >
                  {t('visitDates.remove', { date })}
                </button>
              </li>
            ))}
          </ul>
        </DataSurface>

        {datesRow === null ? null : (
          <div className="lm-supplier__form">
            <Field id={`${ids}-pick`} label={t('visitDates.addLabel')}>
              <input
                id={`${ids}-pick`}
                className="lm-input"
                type="date"
                dir="ltr"
                value={pick}
                onChange={(e) => {
                  setPick(e.currentTarget.value)
                }}
              />
            </Field>
            <button type="button" className="lm-button lm-button--secondary" onClick={addDate}>
              {t('visitDates.add')}
            </button>
            {changed ? <p className="lm-hint">{t('visitDates.unsaved')}</p> : null}
            {removed.length > 0 ? (
              <p className="lm-field__warning">
                {t('visitDates.removeWarning', { name: datesFor.name })}
              </p>
            ) : null}
            <Problem text={datesError} />
            <button
              type="button"
              className="lm-button lm-button--primary"
              disabled={busy}
              onClick={() => {
                void saveDates()
              }}
            >
              {t('visitDates.save')}
            </button>
          </div>
        )}

        <p className="lm-queue-state" role="status">
          {line}
        </p>
      </section>
    )
  }

  const activeTypes = (types ?? []).filter((row) => row.archivedAt === null)
  const shownTypes = showStopped ? (types ?? []) : activeTypes
  const activeResources = (resources ?? []).filter((row) => row.archivedAt === null)
  const shownResources = showStopped ? (resources ?? []) : activeResources

  const missingParts: string[] = []
  if (staffFailed && (resources ?? []).some((row) => row.kind === 'staff')) {
    missingParts.push(t('apptOffers.staffNamesMissing'))
  }
  if (visitsFailed.length > 0) {
    const names = visitsFailed
      .map((id) => (resources ?? []).find((row) => row.id === id)?.name ?? '')
      .filter((name) => name !== '')
      .join(', ')
    missingParts.push(t('apptOffers.visitDatesMissing', { names }))
  }

  return (
    <section className="lm-screen" aria-labelledby={`${ids}-heading`}>
      <h1 id={`${ids}-heading`} className="lm-screen__heading">
        {t('apptOffers.heading')}
      </h1>

      <label className="lm-field" htmlFor={`${ids}-stopped`}>
        <input
          id={`${ids}-stopped`}
          type="checkbox"
          checked={showStopped}
          onChange={(e) => {
            setShowStopped(e.currentTarget.checked)
          }}
        />
        {t('apptSetup.showStopped')}
      </label>

      {/* ------------------------------ types ------------------------------ */}
      <h2 className="lm-screen__heading">{t('apptOffers.typesHeading')}</h2>
      <p className="lm-hint">{t('apptOffers.typesMeans')}</p>
      <p className="lm-hint">{t('apptOffers.typeStoppedMeans')}</p>

      <DataSurface
        state={
          typesFailed
            ? { kind: 'error', message: t('apptOffers.typesLoadFailed'), onRetry: refreshTypes }
            : types === null
              ? { kind: 'loading' }
              : types.length === 0
                ? { kind: 'empty' }
                : shownTypes.length === 0
                  ? { kind: 'filtered' }
                  : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('apptOffers.typesHeading')}
        emptyMessage={t('apptOffers.typesEmpty')}
        filteredMessage={t('apptOffers.typesFiltered')}
        deniedMessage={t('apptSetup.ownerOnly')}
      >
        <ul className="lm-supplier-list">
          {shownTypes.map((row) => {
            const stopped = row.archivedAt !== null
            const unmet = stopped
              ? []
              : row.requiredResourceKinds.filter(
                  (kind) =>
                    resources !== null && !activeResources.some((other) => other.kind === kind),
                )
            return (
              <li key={row.id} className="lm-supplier">
                <span className="lm-supplier__name" dir="auto">
                  {row.name}
                </span>
                <span className="lm-supplier__meta">
                  {t('apptOffers.minutes', { count: row.durationMinutes })}
                  {stopped ? ` · ${t('apptSetup.stoppedNote')}` : ''}
                </span>
                <span className="lm-supplier__meta">
                  {row.requiredResourceKinds.length === 0
                    ? t('apptOffers.typeNeedsNothing')
                    : row.requiredResourceKinds.map((kind) => kindWord(t, kind)).join(', ')}
                </span>
                {unmet.map((kind) => (
                  <span key={kind} className="lm-field__warning">
                    {t('apptOffers.typeNeedsMissing', {
                      kind: kindWord(t, kind),
                      branch: branchName,
                    })}
                  </span>
                ))}
                {typeEditing === row.id ? (
                  <div
                    className="lm-supplier__form"
                    role="group"
                    aria-label={t('apptOffers.typeChange', { name: row.name })}
                  >
                    {typeFields(typeEdit, setTypeEdit, `${ids}-te`)}
                    <Problem text={typeEditError} />
                    <button
                      type="button"
                      className="lm-button lm-button--primary"
                      disabled={busy}
                      onClick={() => {
                        void changeType(row)
                      }}
                    >
                      {t('apptSetup.save')}
                    </button>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setTypeEditing(null)
                        setTypeEditError(null)
                      }}
                    >
                      {t('apptSetup.leave')}
                    </button>
                  </div>
                ) : (
                  <>
                    {stopped ? null : (
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        onClick={() => {
                          setTypeEditing(row.id)
                          setTypeEdit({
                            name: row.name,
                            minutes: String(row.durationMinutes),
                            kinds: row.requiredResourceKinds,
                          })
                          setTypeEditError(null)
                        }}
                      >
                        {t('apptOffers.typeChange', { name: row.name })}
                      </button>
                    )}
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      disabled={busy}
                      onClick={() => {
                        void toggleType(row, !stopped)
                      }}
                    >
                      {t(stopped ? 'apptOffers.typeResume' : 'apptOffers.typeStop', {
                        name: row.name,
                      })}
                    </button>
                  </>
                )}
              </li>
            )
          })}
        </ul>
      </DataSurface>

      <div className="lm-supplier__form" role="group" aria-labelledby={`${ids}-type-add`}>
        <h3 id={`${ids}-type-add`} className="lm-field__label">
          {t('apptOffers.typeAddHeading')}
        </h3>
        {typeFields(typeAdd, setTypeAdd, `${ids}-ta`)}
        <Problem text={typeAddError} />
        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={busy}
          onClick={() => {
            void addType()
          }}
        >
          {t('apptOffers.typeAdd')}
        </button>
      </div>

      {/* ---------------------------- resources ---------------------------- */}
      <h2 className="lm-screen__heading">
        {t('apptOffers.resourcesHeading', { branch: branchName })}
      </h2>
      <p className="lm-hint">{t('apptOffers.resourcesMeans')}</p>
      <p className="lm-hint">{t('apptSetup.branchScope', { branch: branchName })}</p>

      <DataSurface
        state={
          resourcesFailed
            ? {
                kind: 'error',
                message: t('apptOffers.resourcesLoadFailed', { branch: branchName }),
                onRetry: refreshResources,
              }
            : resources === null
              ? { kind: 'loading' }
              : resources.length === 0
                ? { kind: 'empty' }
                : shownResources.length === 0
                  ? { kind: 'filtered' }
                  : missingParts.length > 0
                    ? { kind: 'partial', missing: missingParts.join(', ') }
                    : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('apptOffers.resourcesHeading', { branch: branchName })}
        emptyMessage={t('apptOffers.resourcesEmpty', { branch: branchName })}
        filteredMessage={t('apptOffers.resourcesFiltered', { branch: branchName })}
        deniedMessage={t('apptSetup.ownerOnly')}
      >
        <ul className="lm-supplier-list">
          {shownResources.map((row) => {
            const stopped = row.archivedAt !== null
            const visit = visits[row.id]
            const twin =
              stopped && row.kind === 'staff' ? staffTwin(row.staffId, row.id) : undefined
            return (
              <li key={row.id} className="lm-supplier">
                <span className="lm-supplier__name" dir="auto">
                  {row.name}
                </span>
                <span className="lm-supplier__meta">
                  {kindWord(t, row.kind)}
                  {row.kind === 'staff' && row.staffId !== null
                    ? ` · ${staffName(row.staffId)}`
                    : ''}
                  {stopped ? ` · ${t('apptSetup.stoppedNote')}` : ''}
                </span>
                {row.kind === 'visiting_optometrist' && !stopped && visit !== undefined ? (
                  visit.dates.length === 0 ? (
                    // The trap: nobody can book them on any day, and the row is where it is seen.
                    <span className="lm-field__warning">
                      {t('apptOffers.visitNoDates', { name: row.name })}
                    </span>
                  ) : (
                    <span className="lm-supplier__meta">
                      {t('apptOffers.visitDatesCount', { count: visit.dates.length })}
                    </span>
                  )
                ) : null}
                {renaming === row.id ? (
                  <div
                    className="lm-supplier__form"
                    role="group"
                    aria-label={t('apptOffers.resourceRename', { name: row.name })}
                  >
                    <p className="lm-hint">{t('apptOffers.resourceRenameMeans')}</p>
                    <Field id={`${ids}-rn`} label={t('apptOffers.resourceName')}>
                      <input
                        id={`${ids}-rn`}
                        className="lm-input"
                        dir="auto"
                        autoComplete="off"
                        value={renameTo}
                        onChange={(e) => {
                          setRenameTo(e.currentTarget.value)
                        }}
                      />
                    </Field>
                    <Problem text={renameError} />
                    <button
                      type="button"
                      className="lm-button lm-button--primary"
                      disabled={busy}
                      onClick={() => {
                        void renameResource(row)
                      }}
                    >
                      {t('apptSetup.save')}
                    </button>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setRenaming(null)
                        setRenameError(null)
                      }}
                    >
                      {t('apptSetup.leave')}
                    </button>
                  </div>
                ) : stopped ? (
                  twin !== undefined ? (
                    // The server would refuse this with a 409 that halts the queue, so no button.
                    <span className="lm-field__warning">
                      {t('apptOffers.resourceResumeStaffTaken', {
                        person: staffName(row.staffId),
                        branch: branchName,
                      })}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      disabled={busy}
                      onClick={() => {
                        void toggleResource(row, false)
                      }}
                    >
                      {t('apptOffers.resourceResume', { name: row.name })}
                    </button>
                  )
                ) : (
                  <>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setRenaming(row.id)
                        setRenameTo(row.name)
                        setRenameError(null)
                      }}
                    >
                      {t('apptOffers.resourceRename', { name: row.name })}
                    </button>
                    {row.kind === 'visiting_optometrist' ? (
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        disabled={busy}
                        onClick={() => {
                          openDates(row)
                        }}
                      >
                        {t('apptOffers.visitDatesOpen', { name: row.name })}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      disabled={busy}
                      onClick={() => {
                        void toggleResource(row, true)
                      }}
                    >
                      {t('apptOffers.resourceStop', { name: row.name })}
                    </button>
                  </>
                )}
              </li>
            )
          })}
        </ul>
      </DataSurface>

      <div className="lm-supplier__form" role="group" aria-labelledby={`${ids}-res-add`}>
        <h3 id={`${ids}-res-add`} className="lm-field__label">
          {t('apptOffers.resourceAddHeading')}
        </h3>
        <Field id={`${ids}-rk`} label={t('apptOffers.resourceKind')}>
          <select
            id={`${ids}-rk`}
            className="lm-input"
            value={resKind}
            onChange={(e) => {
              setResKind(e.currentTarget.value)
              setResStaff('')
            }}
          >
            <option value="">{t('apptOffers.resourceKindUnchosen')}</option>
            {RESOURCE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kindWord(t, kind)}
              </option>
            ))}
          </select>
        </Field>
        {resKind === '' ? null : <p className="lm-hint">{kindMeans(t, resKind)}</p>}
        <Field id={`${ids}-rname`} label={t('apptOffers.resourceName')}>
          <input
            id={`${ids}-rname`}
            className="lm-input"
            dir="auto"
            autoComplete="off"
            value={resName}
            onChange={(e) => {
              setResName(e.currentTarget.value)
            }}
          />
        </Field>
        {resKind === 'staff' ? (
          <Field id={`${ids}-rs`} label={t('apptOffers.resourceStaff')}>
            <select
              id={`${ids}-rs`}
              className="lm-input"
              value={resStaff}
              onChange={(e) => {
                setResStaff(e.currentTarget.value)
              }}
            >
              <option value="">{t('apptOffers.resourceKindUnchosen')}</option>
              {(staff ?? [])
                .filter((person) => person.offboardedAt === null)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.displayName}
                  </option>
                ))}
            </select>
          </Field>
        ) : null}
        <Problem text={resAddError} />
        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={busy}
          onClick={() => {
            void addResource()
          }}
        >
          {t('apptOffers.resourceAdd')}
        </button>
      </div>

      <p className="lm-queue-state" role="status">
        {line}
      </p>
    </section>
  )
}
